// Small pieces every POS tab shares.

import type { JSX } from 'react';
import { ApiError } from '../../api/client.js';

export function queryErrorText(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 403) return 'You do not have permission to see this.';
    if (err.status === 404) return 'Nothing found for this outlet.';
    if (err.status === 400) return 'That request was not understood. Check the date and try again.';
    if (err.status === 0) return 'Network error — check your connection.';
  }
  return 'Could not load this. Try again in a moment.';
}

/** The server's error code (`period_has_blockers`, `not_replayable`, …), when it sent one. */
export function apiErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { code?: unknown } | null;
  return body && typeof body === 'object' && typeof body.code === 'string' ? body.code : null;
}

const ACTION_ERROR_TEXT: Readonly<Record<string, string>> = {
  period_has_blockers:
    'Something for this day is still outstanding. Tick the box to close it anyway.',
  period_already_closed: 'This day is already closed.',
  period_already_soft_closed: 'This day is already marked as reviewed.',
  period_not_closed: 'This day is not closed.',
  period_not_found: 'Nothing has been received for that day.',
  exception_not_open: 'This was already acknowledged or resolved. The list has been refreshed.',
  exception_already_resolved: 'This is already resolved.',
  exception_has_no_event: 'This problem is not about a till record, so there is nothing to replay.',
  event_not_dead_lettered: 'That record is no longer set aside, so there is nothing to replay.',
  event_no_longer_available: 'That record is no longer kept, so it cannot be replayed.',
  not_replayable:
    'Replaying would fail the same way. Resolve it instead, or ask the till to issue a correction.',
  pos_not_enabled: 'Tills are not turned on for this restaurant yet.',
  pos_connection_inactive: 'This restaurant’s till connection is not active.',
  pos_disabled: 'Tills are not available on this server yet.',
  device_role_taken:
    'This outlet already has a live till of that kind. Revoke or replace it first.',
  device_not_revocable: 'That till has already ended.',
  device_not_replaceable: 'That till has already ended; set up a new one instead.',
  device_not_recoverable: 'Only an active or suspended till can be recovered.',
  device_not_found: 'That till no longer exists.',
  staff_not_found: 'That person is not on this outlet’s staff list.',
  staff_already_removed: 'That person is already off the till.',
};

/** One sentence for a failed owner action; never the server's raw message. */
export function actionErrorText(
  err: unknown,
  fallback = 'Could not do that. Try again.',
): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 403) return 'You do not have permission to do that.';
    if (err.status === 0) return 'Network error — check your connection.';
    const code = apiErrorCode(err);
    if (code && ACTION_ERROR_TEXT[code]) return ACTION_ERROR_TEXT[code]!;
  }
  return fallback;
}

export function QueryState({
  loading,
  error,
}: {
  readonly loading: boolean;
  readonly error: unknown;
}): JSX.Element | null {
  const text = queryErrorText(error);
  if (text) {
    return (
      <p className="error" role="alert">
        {text}
      </p>
    );
  }
  return loading ? <p className="muted">Loading…</p> : null;
}

export function Chip({
  tone,
  children,
}: {
  readonly tone?: 'ok' | 'warn' | 'bad' | undefined;
  readonly children: string;
}): JSX.Element {
  return <span className={`chip${tone ? ` chip-${tone}` : ''}`}>{children}</span>;
}

export interface TabProps {
  readonly orgId: string;
  readonly outletId: string;
}
